import type { PhotoSuggestion } from "../context/types";

export type BackendActionSuggestion = {
  target_panel: string;
  chip_id: string | null;
  free_text: string;
  rationale: string;
};

export type BackendPhotoSuggestion = {
  observation_id: string;
  onderdeel: string;
  category_id: string;
  category_name: string;
  subcategory: string;
  toelichting: string;
  priority_level: string;
  score: number;
  suggested_actions: BackendActionSuggestion[];
};

type ActionMapping = {
  panel: PhotoSuggestion["target_panel"];
  prefillKey: string | null;
};

const BACKEND_TO_FRONTEND: Record<string, ActionMapping> = {
  floor: { panel: "binnenafwerking", prefillKey: "floor" },
  wall: { panel: "binnenafwerking", prefillKey: "wall" },
  style: { panel: "inrichten", prefillKey: "style" },
  room_type: { panel: "inrichten", prefillKey: "room_type" },
  wall_color: { panel: "muurkleur", prefillKey: "ral_code" },
  free_wish: { panel: "overige_wensen", prefillKey: null },
  furnish: { panel: "inrichten", prefillKey: null },
};

type PanelGroup = {
  prefill: Record<string, string>;
  freeText: string | null;
  label: string;
};

/**
 * Maps backend observation-level suggestions to frontend panel-level suggestions.
 *
 * Groups each observation's suggested_actions by frontend panel, merges chip_id
 * values into structured_prefill, and picks the first non-empty free_text as
 * free_text_prefill.
 */
export function mapBackendSuggestions({
  observations,
}: {
  observations: BackendPhotoSuggestion[];
}): PhotoSuggestion[] {
  if (!Array.isArray(observations)) return [];

  const results: PhotoSuggestion[] = [];

  for (const observation of observations) {
    const {
      suggested_actions,
      observation_id,
      category_name,
      onderdeel,
      toelichting,
    } = observation;

    if (!Array.isArray(suggested_actions)) continue;

    const hasStructuredFurnishing = suggested_actions.some(
      (a) => a.target_panel === "style" || a.target_panel === "room_type",
    );

    const groups = new Map<PhotoSuggestion["target_panel"], PanelGroup>();

    for (const action of suggested_actions) {
      const mapping = BACKEND_TO_FRONTEND[action.target_panel];
      if (!mapping) continue;

      if (action.target_panel === "furnish" && hasStructuredFurnishing)
        continue;

      const { panel, prefillKey } = mapping;

      if (!groups.has(panel)) {
        groups.set(panel, { prefill: {}, freeText: null, label: "" });
      }

      const group = groups.get(panel) as PanelGroup;

      const isWallColorCustom =
        action.target_panel === "wall_color" && !action.chip_id;

      if (isWallColorCustom && action.free_text) {
        group.prefill.custom_text = action.free_text;
      } else {
        if (prefillKey && action.chip_id) {
          group.prefill[prefillKey] = action.chip_id;
        }
        if (action.free_text && group.freeText === null) {
          group.freeText = action.free_text;
        }
      }

      if (action.rationale && !group.label) {
        group.label = action.rationale;
      }
    }

    for (const [panel, group] of groups) {
      results.push({
        id: `${observation_id}_${panel}`,
        target_panel: panel,
        structured_prefill: group.prefill,
        free_text_prefill: group.freeText,
        label: group.label,
        rationale: toelichting,
        source_observation: {
          id: observation_id,
          category_name,
          onderdeel,
          toelichting,
        },
      });
    }
  }

  return results;
}
